/**
 * What the dungeon's controller and a walkable scene's (slice 6a) share: each
 * locale's string table (untranslated keys fall back to English), the sprite
 * sheets of `virtual:ab-assets` with their origins, and loading them into a
 * view while counting what is pending. One home, so neither controller keeps
 * a copy.
 */
import type { SpriteSource } from '@ab/renderer';
import { type Locale, type Strings, localeStrings, locales } from '@ab/session';
import { sprites } from 'virtual:ab-assets';
import { strings } from 'virtual:ab-content';

/** Each locale's table, untranslated keys falling back to the source language (`localeStrings`). */
export const tables = Object.fromEntries(locales.map((l) => [l, localeStrings(strings, l)])) as Readonly<Record<Locale, Strings>>;

/** The sprite sheets the shell loads: ids the view installed (landed), where each came from, and how many loads are still pending. */
export type SpritesReport = { readonly loaded: readonly string[]; readonly origins: Readonly<Record<string, 'shipped' | 'fallback'>>; readonly pending: number };

export const spriteSources: readonly SpriteSource[] = Object.values(sprites);
export const spriteOrigins: SpritesReport['origins'] = Object.fromEntries(spriteSources.map((s) => [s.id, s.origin]));

/** Loads sheets into a view and keeps the shell's report (installed ids, pending loads) current. */
export const spriteLoader = (): { load(view: { loadSprites(sources: readonly SpriteSource[]): Promise<readonly string[]> }, sources?: readonly SpriteSource[]): void; report(): SpritesReport } => {
  let loaded: readonly string[] = [];
  let pending = 0;
  return {
    load(view, sources = spriteSources) {
      pending += 1;
      view
        .loadSprites(sources)
        .then(
          (installed) => {
            loaded = installed;
          },
          (e: unknown) => {
            console.error(`sprites did not load: ${String(e)}`);
          },
        )
        .finally(() => {
          pending -= 1;
        });
    },
    report: () => ({ loaded, origins: spriteOrigins, pending }),
  };
};
